import React, { useState } from 'react';
import { Certificate, User } from '../types';
import { Search, Award, Upload, CheckCircle2, ShieldCheck, AlertCircle, X, FileText, Loader2 } from 'lucide-react';
import { api, apiForm } from '../api';

interface CertificateSectionProps {
  user: User | null;
  onRequireLogin: () => void;
}

export const CertificateSection: React.FC<CertificateSectionProps> = ({ user, onRequireLogin }) => {
  const [tab, setTab] = useState<'query' | 'upload'>('query');
  const [keyword, setKeyword] = useState('');
  const [results, setResults] = useState<Certificate[] | null>(null);
  const [searched, setSearched] = useState(false);
  const [searching, setSearching] = useState(false);

  // 上传表单
  const [holderName, setHolderName] = useState('');
  const [idCard, setIdCard] = useState('');
  const [type, setType] = useState('救生员资格证');
  const [level, setLevel] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [uploadMsg, setUploadMsg] = useState('');
  const [uploadErr, setUploadErr] = useState('');
  const [uploading, setUploading] = useState(false);

  const doSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!keyword.trim()) return;
    setSearching(true);
    setSearched(true);
    try {
      const data = await api<Certificate[]>('query_certificate', { keyword: keyword.trim() });
      setResults(data);
    } catch (ex: any) {
      setResults([]);
      alert(ex.message);
    } finally {
      setSearching(false);
    }
  };

  const doUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) { onRequireLogin(); return; }
    setUploadMsg(''); setUploadErr('');
    if (!holderName.trim() || !idCard.trim()) { setUploadErr('请填写持证人姓名与身份证号'); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('holderName', holderName.trim());
      form.append('idCard', idCard.trim());
      form.append('type', type);
      form.append('level', level.trim());
      if (file) form.append('file', file);
      const data = await apiForm<{ certNo: string }>('upload_certificate', form);
      setUploadMsg(`提交成功！受理编号 ${data.certNo}，请等待协会审核。`);
      setHolderName(''); setIdCard(''); setLevel(''); setFile(null);
    } catch (ex: any) {
      setUploadErr(ex.message || '提交失败');
    } finally {
      setUploading(false);
    }
  };

  return (
    <section className="py-12 bg-slate-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-sky-100/80 text-sky-700 rounded-full text-xs font-bold mb-3">
            <Award className="w-4 h-4" /><span>长顺县游泳协会 · 权威证书核验中心</span>
          </div>
          <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight">证书查询与资质认证</h2>
          <p className="text-slate-600 text-sm mt-2">输入证书编号、持证人姓名或身份证号，查询协会核发的游泳裁判员、救生员、等级运动员等证书。</p>
        </div>

        {/* Tab */}
        <div className="flex justify-center gap-2 mb-8">
          <button onClick={() => setTab('query')}
            className={`px-6 py-2.5 rounded-xl text-sm font-bold transition-all ${tab === 'query' ? 'bg-sky-600 text-white shadow-lg shadow-sky-600/25' : 'bg-white text-slate-600 border border-slate-200 hover:border-sky-300'}`}>
            <Search className="w-4 h-4 inline mr-1" />证书核验查询
          </button>
          <button onClick={() => setTab('upload')}
            className={`px-6 py-2.5 rounded-xl text-sm font-bold transition-all ${tab === 'upload' ? 'bg-sky-600 text-white shadow-lg shadow-sky-600/25' : 'bg-white text-slate-600 border border-slate-200 hover:border-sky-300'}`}>
            <Upload className="w-4 h-4 inline mr-1" />证书上传备案
          </button>
        </div>

        {tab === 'query' && (
          <div className="max-w-3xl mx-auto space-y-6">
            <form onSubmit={doSearch} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm flex flex-col sm:flex-row gap-3">
              <div className="flex-1 flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-4 focus-within:ring-2 focus-within:ring-sky-500">
                <Search className="w-4 h-4 text-slate-400 shrink-0" />
                <input value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder="输入证书编号 / 姓名 / 身份证号"
                  className="w-full bg-transparent py-3 text-sm focus:outline-none" />
              </div>
              <button type="submit" disabled={searching}
                className="px-8 py-3 bg-gradient-to-r from-sky-600 to-blue-600 text-white font-bold text-sm rounded-xl shadow disabled:opacity-60">
                {searching ? '查询中...' : '立即核验'}
              </button>
            </form>

            {searched && (
              <div className="space-y-3">
                {results && results.length > 0 ? results.map((c) => (
                  <div key={c.id} className="bg-white rounded-2xl border border-emerald-200 p-5 shadow-sm flex items-start gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
                      <ShieldCheck className="w-7 h-7" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-black text-slate-900">{c.holder_name}</span>
                        <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-[10px] font-bold rounded">核验通过 · 真实有效</span>
                      </div>
                      <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs text-slate-600">
                        <p><b className="text-slate-900">证书编号：</b><span className="font-mono">{c.cert_no}</span></p>
                        <p><b className="text-slate-900">证书类型：</b>{c.type}</p>
                        <p><b className="text-slate-900">等级：</b>{c.level}</p>
                        <p><b className="text-slate-900">发证日期：</b>{c.issue_date}</p>
                        <p className="col-span-full"><b className="text-slate-900">发证机构：</b>{c.issuer}</p>
                      </div>
                    </div>
                  </div>
                )) : (
                  <div className="bg-white rounded-2xl border border-amber-200 p-6 text-center">
                    <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-2" />
                    <p className="text-sm font-bold text-slate-800">未查询到匹配的证书信息</p>
                    <p className="text-xs text-slate-500 mt-1">请核对输入的证书编号或身份证号，或联系协会办公室核实。</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {tab === 'upload' && (
          <div className="max-w-2xl mx-auto bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 shadow-sm">
            {!user ? (
              <div className="text-center py-10 space-y-4">
                <FileText className="w-12 h-12 text-slate-300 mx-auto" />
                <p className="text-sm font-bold text-slate-700">证书上传备案需要登录账号</p>
                <button onClick={onRequireLogin} className="px-6 py-2.5 bg-sky-600 text-white text-sm font-bold rounded-xl">去登录 / 注册</button>
              </div>
            ) : (
              <form onSubmit={doUpload} className="space-y-4">
                <h3 className="text-base font-black text-slate-900">证书上传备案（管理员审核后对外可查）</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-1">持证人姓名 *</label>
                    <input type="text" value={holderName} onChange={(e) => setHolderName(e.target.value)} required
                      className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-sky-500 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-1">身份证号 *</label>
                    <input type="text" value={idCard} onChange={(e) => setIdCard(e.target.value)} required maxLength={18}
                      className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-sky-500 text-sm font-mono" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-1">证书类型</label>
                    <select value={type} onChange={(e) => setType(e.target.value)}
                      className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-sky-500 text-sm">
                      <option>游泳裁判员</option><option>救生员资格证</option><option>青少年等级运动员</option><option>教练员证书</option><option>赛事获奖证书</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-1">等级/级别</label>
                    <input type="text" value={level} onChange={(e) => setLevel(e.target.value)} placeholder="如：国家二级 / 中级救生员"
                      className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-sky-500 text-sm" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">证书照片/扫描件（jpg/png/pdf，≤10MB）</label>
                  <input type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" onChange={(e) => setFile(e.target.files?.[0] || null)}
                    className="w-full text-sm text-slate-500 file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0 file:bg-sky-50 file:text-sky-700 file:text-xs file:font-bold hover:file:bg-sky-100" />
                </div>
                {uploadMsg && <p className="text-xs text-emerald-700 font-bold bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 shrink-0" />{uploadMsg}</p>}
                {uploadErr && <p className="text-xs text-red-600 font-semibold bg-red-50 border border-red-200 rounded-xl px-3 py-2 flex items-center gap-1.5"><AlertCircle className="w-4 h-4 shrink-0" />{uploadErr}</p>}
                <button type="submit" disabled={uploading}
                  className="w-full py-3 bg-gradient-to-r from-emerald-600 to-sky-600 text-white font-bold text-sm rounded-xl shadow disabled:opacity-60 flex items-center justify-center gap-2">
                  {uploading ? <><Loader2 className="w-4 h-4 animate-spin" />上传中...</> : <><Upload className="w-4 h-4" />提交审核</>}
                </button>
              </form>
            )}
          </div>
        )}
      </div>
    </section>
  );
};
